import axios from 'axios';
import throttle from 'lodash.throttle';

import config from '@/utility/config';

import { getAxiosError, getAxiosErrorMsg } from '../helpers';
import adminProtectedAxiosRequest from '../helpers/adminProtectedAxiosRequest';

const { communityApiBasePath } = config;

const INTERVAL_FOR_THROTTLE = 1000;
const INTERVAL_FOR_UPLOAD_PROGRESS = 15;
export const getCommunityFolderList = async (payload) => {
  try {
    const { type = '', communityId } = payload;
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folders?type=${type}`;
    const res = await adminProtectedAxiosRequest.get(path, {
      withCredentials: true
    });
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

export const createCommunityFolder = async ({ communityId, payload }) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder`;
    const res = await adminProtectedAxiosRequest.post(path, payload);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

export const editCommunityFolder = async ({
  communityId,
  payload,
  folderId
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder/${folderId}`;
    const res = await adminProtectedAxiosRequest.put(path, payload);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

export const deleteCommunityFolder = async ({ communityId, folderId }) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder/${folderId}`;
    const res = await adminProtectedAxiosRequest.delete(path);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosErrorMsg(error);
  }
};

export const updatePublishCommunityFolder = async ({
  communityId,
  folderId,
  payload
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder/${folderId}/save-and-publish`;
    const res = await adminProtectedAxiosRequest.post(path, payload);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

export const getCommunityFolderItems = async ({
  folderId,
  communityId
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder/${folderId}`;
    const res = await adminProtectedAxiosRequest.get(path);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

export const getCommunityFolderItem = async ({
  communityId,
  folderItemId
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder-item/${folderItemId}`;
    const res = await adminProtectedAxiosRequest.get(path);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

export const deleteCommunityFolderItem = ({
  communityId,
  folderItemId
}) => {
  const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder-item/${folderItemId}`;
  return adminProtectedAxiosRequest.delete(path);
};

export const createCommunityFolderItem = async ({
  communityId,
  folderId,
  payload
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder/${folderId}/folder-item`;
    const res = await adminProtectedAxiosRequest.post(path, payload);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

/* payload = {
  mimeType: '/pdf',
  numberOfChunks: 100,
  orginalFileName: 'randomname.jpg',
  typeOfFile: file,
}
*/
export const fetchSignedUrlForFolderItem = async ({
  communityId,
  folderId,
  payload
}) => {
  try {
    let {
      mimeType,
      numberOfChunks,
      originalFileName,
      typeOfFile,
      isMultipart
    } = payload;
    mimeType = encodeURIComponent(mimeType);
    originalFileName = encodeURIComponent(originalFileName);
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder/${folderId}/folder-item-upload?mimetype=${mimeType}&numberOfChunks=${numberOfChunks}&originalname=${originalFileName}&type=${typeOfFile}${
      isMultipart ? `&isMultipart=${isMultipart}` : ''
    }`;

    const res = await adminProtectedAxiosRequest.get(path);

    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

export const mergeSignedUrlForFolderItemToS3 = async ({
  communityId,
  folderId,
  payload
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder/${folderId}/folder-item-upload`;
    const res = await adminProtectedAxiosRequest.post(path, payload);
    return { data: res.data, error: null };
  } catch (error) {
    return { error: error?.response?.data?.message ?? error.message };
  }
};

export const uploadFileChunksToS3 = async ({
  clientId,
  signedUrl,
  blob,
  payload,
  indexOfRecord,
  onProgressUpdate,
  logEvent
}) => {
  try {
    const data = await axios.put(signedUrl, blob, {
      ...payload,
      ...(onProgressUpdate
        ? {
            onUploadProgress: (progressEvent) => {
              const throttledCall = throttle(() => {
                // call if the progress for every 5% of the file is uploaded
                if (
                  Math.floor(
                    (progressEvent.loaded / progressEvent.total) * 100
                  ) %
                    INTERVAL_FOR_UPLOAD_PROGRESS ===
                  0
                ) {
                  onProgressUpdate(clientId, indexOfRecord, progressEvent);
                }
              }, INTERVAL_FOR_THROTTLE);
              throttledCall();
            }
          }
        : {}),
      headers: {
        'Content-Type': encodeURI(blob.type)
      }
    });
    logEvent(data);
    return { data: data, error: null };
  } catch (error) {
    logEvent(error);
    return {
      ...getAxiosError(error),
      errorIndex: indexOfRecord
    };
  }
};

export const getSignedURLForASingleFilePart = async ({
  communityId,
  folderId,
  signedUrlId,
  partNumber
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder/${folderId}/folder-item-upload?signedUrlId=${signedUrlId}&partNumber=${partNumber}`;
    const res = await adminProtectedAxiosRequest.get(path);
    return { data: res.data, error: null };
  } catch (error) {
    return { error: error?.response?.data?.message ?? error.message };
  }
};
export const updateCommunityFolderItem = async ({
  communityId,
  folderItemId,
  payload
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder-item/${folderItemId}`;
    const res = await adminProtectedAxiosRequest.put(path, payload);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

export const updateCommunityFolderOrder = async ({
  communityId,
  payload
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder/updateIndices`;
    const res = await adminProtectedAxiosRequest.post(path, payload);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

export const updateCommunityFolderItemsOrder = async ({
  communityId,
  folderId,
  payload
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folder/${folderId}/reorder-folderItems`;
    const res = await adminProtectedAxiosRequest.put(path, payload);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosErrorMsg(error);
  }
};

export const checkCommunityStorageService = async ({
  communityId,
  uploadingFileSize
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/check-community-storage/${uploadingFileSize}`;
    const res = await adminProtectedAxiosRequest.post(path);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

export const getAudience = async ({
  communityId,
  folderId,
  searchTerm,
  sortBy,
  sortOrder,
  filter,
  pageNumber,
  pageSize
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/folders/${folderId}/viewers?status=${filter}&search=${searchTerm}&pageSize=${pageSize}&pageNo=${pageNumber}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
    const res = await adminProtectedAxiosRequest.get(path);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};

export const getAbandonedCheckouts = async ({
  communityId,
  entityId,
  entityType,
  searchTerm,
  sortBy,
  sortOrder,
  pageNumber,
  pageSize
}) => {
  try {
    const path = `${communityApiBasePath}/api/v1/communities/${communityId}/entity/${entityId}/abandoned-checkout?entityType=${entityType}&search=${searchTerm}&pageSize=${pageSize}&pageNo=${pageNumber}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
    const res = await adminProtectedAxiosRequest.get(path);
    if (res?.error) throw new Error(res.error);
    return { data: res.data, error: null };
  } catch (error) {
    return getAxiosError(error);
  }
};
